import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { API, StatCard, Field, inputCls, Layers, Check, DollarSign, ArrowDownUp } from './shared';

export default function PositionsTab() {
  const [posStats, setPosStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [mintAddr, setMintAddr] = useState('');
  const [mintAmount, setMintAmount] = useState('');
  const [mintLoading, setMintLoading] = useState(false);
  const [mintResult, setMintResult] = useState(null);

  const refresh = useCallback(() => {
    fetch(`${API}/api/agent-credit/positions/stats/overview`).then(r => r.json()).then(setPosStats).catch(() => {});
    fetch(`${API}/api/agent-credit/positions/marketplace/listings`).then(r => r.json()).then(d => setListings(d.listings || [])).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const mint = async (e) => {
    e.preventDefault();
    setMintLoading(true); setMintResult(null);
    try {
      const res = await fetch(`${API}/api/agent-credit/positions/mint`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lender_address: mintAddr.trim(), amount: parseFloat(mintAmount) }),
      });
      const data = await res.json();
      setMintResult(res.ok ? { type: 'success', data } : { type: 'error', msg: data.detail });
      if (res.ok) refresh();
    } catch { setMintResult({ type: 'error', msg: 'Network error' }); }
    finally { setMintLoading(false); }
  };

  return (
    <div data-testid="positions-tab">
      {posStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Layers} label="Total Minted" value={posStats.total_minted} color="purple" />
          <StatCard icon={Check} label="Active Positions" value={posStats.active_positions} color="green" />
          <StatCard icon={DollarSign} label="Tokenized Value" value={`$${(posStats.total_tokenized_value || 0).toLocaleString()}`} color="blue" />
          <StatCard icon={ArrowDownUp} label="Total Transfers" value={posStats.total_transfers} color="orange" />
        </div>
      )}

      <div className="rounded-2xl border border-purple-500/20 bg-purple-500/[0.03] p-6 mb-8">
        <h3 className="font-semibold text-lg mb-2 flex items-center gap-2"><Layers className="w-5 h-5 text-purple-400" /> Mint Credit Position NFT</h3>
        <p className="text-xs text-gray-500 mb-4">Tokenize a portion of your Credit Marketplace position into a tradeable NFT with fee claims.</p>
        <form onSubmit={mint} className="grid md:grid-cols-3 gap-4">
          <Field label="Lender Address"><input value={mintAddr} onChange={e => setMintAddr(e.target.value)} placeholder="0x..." className={inputCls} required data-testid="nft-mint-address" /></Field>
          <Field label="Amount to Tokenize (USD)"><input value={mintAmount} onChange={e => setMintAmount(e.target.value)} placeholder="5000" type="number" min="0" step="0.01" className={inputCls} required data-testid="nft-mint-amount" /></Field>
          <div className="flex items-end">
            <Button type="submit" disabled={mintLoading} className="w-full h-12 bg-gradient-to-r from-purple-600 to-pink-600" data-testid="nft-mint-btn">
              {mintLoading ? 'Minting...' : 'Mint Position NFT'}
            </Button>
          </div>
        </form>
        {mintResult && (
          <div className={`mt-4 p-4 rounded-lg border text-sm ${mintResult.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`} data-testid="nft-mint-result">
            {mintResult.type === 'success' ? (
              <div>
                <p className="flex items-center gap-2"><Check className="w-4 h-4" /> {mintResult.data.message}</p>
                <p className="text-xs font-mono text-gray-500 mt-1">Token ID: {mintResult.data.token_id} | Value: ${mintResult.data.face_value?.toLocaleString()} | Yield at Mint: {mintResult.data.yield_rate_at_mint}%</p>
              </div>
            ) : (<p>{mintResult.msg}</p>)}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden" data-testid="nft-listings">
        <div className="px-5 py-4 border-b border-white/5"><h3 className="font-semibold flex items-center gap-2"><Layers className="w-4 h-4 text-purple-400" /> Active Position NFTs</h3></div>
        {listings.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-xs text-gray-500 uppercase border-b border-white/5">
                <th className="px-4 py-3 text-left">Token ID</th><th className="px-4 py-3 text-left">Owner</th><th className="px-4 py-3 text-right">Face Value</th><th className="px-4 py-3 text-right">Yield Rate</th><th className="px-4 py-3 text-right">Transfers</th>
              </tr></thead>
              <tbody className="divide-y divide-white/[0.03]">
                {listings.map(nft => (
                  <tr key={nft.token_id} className="hover:bg-white/[0.02]">
                    <td className="px-4 py-3 font-mono text-xs text-purple-400">{nft.token_id}</td>
                    <td className="px-4 py-3 font-mono text-xs">{nft.owner_address?.slice(0,10)}...{nft.owner_address?.slice(-4)}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold">${nft.face_value?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-mono text-green-400">{nft.yield_rate_at_mint}%</td>
                    <td className="px-4 py-3 text-right font-mono">{nft.transfer_history?.length || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-12 text-center"><Layers className="w-8 h-8 text-gray-700 mx-auto mb-3" /><p className="text-sm text-gray-600">No position NFTs minted yet.</p></div>
        )}
      </div>
    </div>
  );
}
